import { IQueryCommandOptions } from '@llws/dynamic-router-utils';
import {
  ITourUserSettings,
  TScheduleItem,
  TTimeslot,
  TToursAvailability,
} from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import vm from '@/main';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { timeOptions } from '@/shared/helpers/time-options';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { ITimeSlot } from '@/shared/interfaces/time-slot';
import { IAgentSchedulePayload } from '@/shared/interfaces/tours';
import { adminRequest } from '@/shared/request-configs/admin-request';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface IToursUserAvailability {
  availability: TToursAvailability,
  exceptions: ITimeSlot[],
}
interface IToursUserAvailabilityState {
  agentTourUserSettings: ITourUserSettings,
  requestedYearMonth: string,
  requestedDuration: number,
  timeslotBufferBefore: number,
  timeslotBufferAfter: number,
  includeBufferBefore: number,
  includeBufferAfter: number,
  userAvailability: IToursUserAvailability | undefined,
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {};

const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<IToursUserAvailability>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const dateOptions = (yearMonth: string): string[] => {
  const days = dayjs(yearMonth).daysInMonth();
  const options = [];
  for (let i = 1; i <= days; i++) {
    if (dayjs(yearMonth).date(i).add(1, 'second').isAfter(dayjs().startOf('day'))) {
      options.push(dayjs(yearMonth).date(i).format('YYYY-MM-DD'));
    }
  }
  return options;
};

// date: YYYY-MM-DD
const dateAvailability = (
  date: string,
  duration: number,
  schedule: Array<TScheduleItem>,
  exceptions: Array<TTimeslot>,
  timeslotBufferBefore: number,
  timeslotBufferAfter: number,
) => {
  // get the day's schedule and exceptions
  const scheduleOfTheDay = schedule.find((item) => item.day === dayjs(date).day());
  const exceptionsOfTheDay = exceptions.filter((excepction) => dayjs(excepction.startTime).format('YYYY-MM-DD') === date)
    .sort((expA, expB) => (dayjs(expA.startTime).isBefore(dayjs(expB.startTime)) ? -1 : 1));
  const availableDateTimeOptions: string[] = [];

  if (scheduleOfTheDay) {
    timeOptions().forEach((option) => {
      // the time option with a specific duration must be in the schedule and out of any exception
      const dateTime = dayjs(`${date} ${option}`);
      const timeSlotsAvailable: TTimeslot[] = scheduleOfTheDay.timeslots.filter(
        (slot) => (dayjs(dateTime).isAfter(dayjs())
          && dayjs(`${date} ${slot.startTime}`).isSameOrBefore(dateTime)
          && dayjs(`${date} ${slot.endTime}`).isSameOrAfter(dateTime.add(duration, 'minute'))),
      );

      if (timeSlotsAvailable.length > 0) {
        // Here we want to apply the buffer before/after values to each date like we do on BE for time widgets
        // Same function as buildDayTimeSlots packages\apis\tour-api\src\router\widget\helpers\timeSlotHelpers.ts
        const conflictExceptions = exceptionsOfTheDay.filter(
          (exception) => {
            const startWithBuffer = dayjs(exception.startTime).subtract(timeslotBufferBefore, 'minutes');
            const endWithBuffer = dayjs(exception.endTime).add(timeslotBufferAfter, 'minutes');
            return (dateTime.isBetween(startWithBuffer, endWithBuffer, 'minutes', '[)')
              || dateTime.add(duration, 'minute').isBetween(startWithBuffer, endWithBuffer, 'minutes', '(]'));
          },
        );

        if (conflictExceptions.length === 0) {
          availableDateTimeOptions.push(`${date} ${option}`);
        }
      }
    });
  }
  return availableDateTimeOptions;
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    agentTourUserSettings: {} as ITourUserSettings,
    requestedYearMonth: '' as string,
    requestedDuration: 0 as number,
    userAvailability: {} as IToursUserAvailability,
    timeslotBufferBefore: 0 as number,
    timeslotBufferAfter: 0 as number,
  },

  getters: {
    ...base.getters,

    agentTourUserSettings(state: IToursUserAvailabilityState) { return state.agentTourUserSettings; },

    userAvailability(state: IToursUserAvailabilityState) { return state.userAvailability ? state.userAvailability : undefined; },

    // yearMonth: YYYY-MM
    agentAvailability(state: IToursUserAvailabilityState): Record<string, Array<string>> {
      if (
        state.requestedYearMonth
        && state.requestedDuration
        && state.agentTourUserSettings
        && state.agentTourUserSettings.availability
        && state.agentTourUserSettings.availability.schedule
      ) {
        const dates = dateOptions(state.requestedYearMonth);
        const { exceptions } = state.agentTourUserSettings as ITourUserSettings & Record<'exceptions', Record<string, TTimeslot>>;
        const exceptionsArray: TTimeslot[] = Object.values(exceptions);
        const agentAvailability: Record<string, Array<string>> = {};
        // If the building has buffer time before/after, we want to add those timers to the daily schedule
        // is date available ?
        dates.forEach((date) => {
          const availability = dateAvailability(
            date,
            state.requestedDuration,
            state.agentTourUserSettings.availability!.schedule,
            exceptionsArray,
            state.timeslotBufferBefore,
            state.timeslotBufferAfter,
          );

          if (availability.length > 0) {
            agentAvailability[date] = availability;
          }
        });
        return agentAvailability;
      }
      return {};
    },
  },

  mutations: {
    ...base.mutations,
    setRequestedYearMonth(state: IToursUserAvailabilityState, yearMonth: string) {
      state.requestedYearMonth = yearMonth;
    },
    setRequestedDuration(state: IToursUserAvailabilityState, duration: number) {
      state.requestedDuration = duration;
    },
    setBufferBefore(state: IToursUserAvailabilityState, bufferBefore: number) {
      state.timeslotBufferBefore = bufferBefore;
    },
    setBufferAfter(state: IToursUserAvailabilityState, bufferAfter: number) {
      state.timeslotBufferAfter = bufferAfter;
    },
    includeBufferBefore(state: IToursUserAvailabilityState, bufferBefore: number) {
      state.includeBufferBefore = bufferBefore;
    },
    includeBufferAfter(state: IToursUserAvailabilityState, bufferAfter: number) {
      state.includeBufferAfter = bufferAfter;
    },
    setUserAvailability(state: IToursUserAvailabilityState, payload: IToursUserAvailability) {
      state.userAvailability = payload;
    },
    setAgentTourUserSettings(state: IToursUserAvailabilityState, payload: ITourUserSettings) {
      state.agentTourUserSettings = payload;
    },
  },

  actions: {
    ...base.actions,

    getAgentSchedule: async (ctx: IAction<IStateStrict<IToursUserAvailability>>, payload: IAgentSchedulePayload) => {
      const buildingIdParam = payload.buildingId ? `?buildingId=${payload.buildingId}` : '';
      const response = await adminRequest.get(vm()?.$rsGlobals.$hasToursRestricted()
        ? `${agentGatewayResourcePathAgent}/availability/${payload.date}${payload.override ? '/override' : ''}${buildingIdParam}`
        : `${adminGatewayResourcePathAdmin}/availability/${payload.agentId}/${payload.date}${payload.override ? '/override' : ''}${buildingIdParam}`);
      ctx.commit('setAgentTourUserSettings', response);
      return response;
    },

    getUserAvailability: async (ctx: IAction<IStateStrict<IToursUserAvailability>>, payload: { date: string, userId?: number; }) => {
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? `${agentGatewayResourcePathAgent}/availability/${payload.date}/` : `${adminGatewayResourcePathAdmin}/availability/${payload.userId}/${payload.date}/`}`);
      ctx.commit('setUserAvailability', response);
      return response.data;
    },
  },
};
